/**
 * C3 — Types du SIRH côté interface.
 *
 * Ils décrivent ce que le serveur RENVOIE. Aucun d'eux ne sert à calculer :
 * le nombre de jours, l'état et le périmètre sont des réponses, pas des
 * déductions du navigateur.
 */

export type LeaveState =
  | 'draft'
  | 'submitted'
  | 'in_review'
  | 'approved'
  | 'applied'
  | 'rejected'
  | 'cancelled';

export type LeaveCommand =
  | 'submit'
  | 'approve_level'
  | 'approve_final'
  | 'apply'
  | 'reject'
  | 'cancel'
  | 'return_to_draft';

export interface StateMachine {
  states: LeaveState[];
  terminalStates: LeaveState[];
  transitions: { command: LeaveCommand; from: LeaveState; to: LeaveState }[];
}

export interface SirhMe {
  onDate: string;
  ownEmploymentUuids: string[];
  managedEmploymentUuids: string[];
  isOrganisationAdmin: boolean;
  /** Capacités calculées par l'API à partir de l'acteur et des modules actifs. */
  capabilities?: {
    viewOwnLeave: boolean;
    reviewLeaveRequests: boolean;
  };
}

export type LeaveDayReason =
  | 'compte'
  | 'demi_journee'
  | 'hors_jour_ouvrable'
  | 'non_travaille_au_contrat'
  | 'ferie_chome'
  | 'avant_premier_jour_travaille'
  | 'prolongation_avant_reprise';

export interface LeaveDayLine {
  date: string;
  weekday: number;
  counted: number;
  reason: LeaveDayReason;
  holidayLabel?: string;
  contractReference: string;
}

export interface LeaveCounting {
  totalDays: number;
  countingMode: 'ouvrables' | 'ouvres';
  policyVersion: number;
  countedFrom: string | null;
  countedTo: string | null;
  days: LeaveDayLine[];
  accrualShares: { periodStart: string; periodEnd: string; days: number }[];
  notes: string[];
}

export interface LeaveRequest {
  uuid: string;
  employmentUuid: string;
  folderUuid: string;
  leaveTypeUuid: string;
  startsOn: string;
  endsOn: string;
  startBoundary: string;
  endBoundary: string;
  countedDays: string;
  countingMode: string;
  policyVersion: number;
  state: LeaveState;
  version: number;
  currentLevel: number;
  employeeComment?: string | null;
  justificationReference?: string | null;
  computationNotes?: string[] | null;
  /** Nom lisible du salarié, ajouté par l'API dans la file d'un responsable. */
  personLabel?: string;
  leaveTypeLabel?: string;
  leaveTypeLegalCategory?: 'paid_leave' | 'sickness' | 'other' | null;
  canRestorePaidLeaveOverlap?: boolean;
  overlapRestoredAt?: string | null;
}

export interface LeaveType {
  uuid: string;
  code: string;
  label: string;
  consumesEntitlement: boolean;
  requiresJustification?: boolean;
  legalCategory?: 'paid_leave' | 'sickness' | 'other' | null;
}

export interface RestoredPaidLeaveOverlap {
  sicknessRequestUuid: string;
  paidLeaveRequestUuids: string[];
  restoredDays: number;
  alreadyRestoredDays: number;
}

export interface LeaveEntitlement {
  uuid: string;
  employmentUuid: string;
  leaveTypeUuid: string;
  periodStart: string;
  periodEnd: string;
  acquiredDays: string;
  takenDays: string;
  pendingDays: string;
}

export interface PersonalOverview {
  requests: LeaveRequest[];
  entitlements: LeaveEntitlement[];
  leaveTypes: LeaveType[];
}

export interface VariablePeriod {
  uuid: string;
  folderUuid: string;
  period: string;
  state: 'ouverte' | 'gelee' | 'transmise' | 'rouverte';
  version: number;
  reopenReason?: string | null;
}

export interface TransmissionRejection {
  variableUuid: string | null;
  code:
    | 'salarie_non_resolu'
    | 'unite_invalide'
    | 'quantite_absente'
    | 'periode_absente'
    | 'emploi_inconnu';
  message: string;
}

export interface TransmissionCheck {
  transmittable: boolean;
  rejections: TransmissionRejection[];
}

/** Libellés français des motifs de décompte, pour la trace jour par jour. */
export const LEAVE_DAY_REASON_LABELS: Record<LeaveDayReason, string> = {
  compte: 'Jour décompté',
  demi_journee: 'Demi-journée',
  hors_jour_ouvrable: 'Jour non ouvrable',
  non_travaille_au_contrat: 'Non travaillé au contrat',
  ferie_chome: 'Jour férié chômé',
  avant_premier_jour_travaille: 'Avant le premier jour travaillé',
  prolongation_avant_reprise: 'Jour ouvrable avant la reprise',
};

/** Libellés français des états. La liste des états, elle, vient du serveur. */
export const LEAVE_STATE_LABELS: Record<LeaveState, string> = {
  draft: 'Brouillon',
  submitted: 'À approuver',
  in_review: 'En cours d’approbation',
  approved: 'Approuvée',
  applied: 'Décomptée',
  rejected: 'Refusée',
  cancelled: 'Annulée',
};
